import { resolveModelVistaNodePath } from "../logic/modelvista_extension.js";
import { readGlb, writeGlb, embedThumbnails } from "./glb.js";
import { materialSlots } from "./materials.js";

export const EXTENSION = "INNOWORKS_configurables";
export const EDITABLE_KINDS = ["Activation", "Transform", "Camera", "Material", "Animation", "Group"];
export const KIND_LABELS = { Activation: "Visibility", Transform: "Transform", Camera: "Camera", Material: "Material", Group: "Group", Shader: "Shader", Light: "Light", Animation: "Animation", Unknown: "Unsupported" };
const KINDS = [...EDITABLE_KINDS, "Shader", "Light"];
const copy = value => structuredClone(value);
const id = () => crypto.randomUUID();
export const dataOf = set => set.raw[set.kind];
export const nameOf = set => dataOf(set)?.ModelVistaName || KIND_LABELS[set.kind] || "State set";
export const optionName = option => option.raw.DisplayName || "Untitled option";

// Authoring preview state belongs to the draft, never to the exported extension.
export const deferredStateIndices = doc => doc.sets.flatMap((set, index) => (doc.deferredStateIds ?? []).includes(set.id) ? [index] : []);

// Indices match the shared runtime: native glTF clips take precedence over
// legacy Unity curves at the same index. FBX clips become native glTF on import.
export function animationClips(doc, set = null) {
    const native = doc.json.animations ?? [], legacy = (set ? dataOf(set)?.LegacyClips : null) ?? [];
    return Array.from({ length: Math.max(native.length, legacy.length) }, (_, index) => {
        const clip = native[index];
        if (!clip) return { index, name: legacy[index]?.Name || `Legacy clip ${index + 1}`, legacy: true, duration: null };
        const ends = (clip.samplers ?? []).map(sampler => doc.json.accessors?.[sampler.input]?.max?.[0]).filter(Number.isFinite);
        return { index, name: clip.name || `Animation ${index + 1}`, legacy: false, duration: ends.length ? Math.max(...ends) : null };
    });
}

function kindOf(raw) {
    return KINDS.find(kind => raw[kind] && typeof raw[kind] === "object") ?? "Unknown";
}

function rootIndex(json) {
    const rootName = json.extensions?.[EXTENSION]?.RootName;
    if (rootName) {
        const index = json.nodes?.findIndex(node => node.name === rootName);
        if (index >= 0) return index;
    }
    return json.scenes?.[json.scene ?? 0]?.nodes?.[0] ?? null;
}

function uniqueName(name, used) {
    const base = String(name || "Part").replaceAll("/", "_");
    let value = base, index = 2;
    while (used.has(value)) value = `${base} (${index++})`;
    used.add(value); return value;
}

export function nodeTree(json) {
    const root = rootIndex(json);
    if (!Number.isInteger(root)) throw new Error("The model does not contain a scene hierarchy.");
    const result = [], visited = new Set();
    function visit(index, path, depth) {
        if (visited.has(index)) throw new Error("The model hierarchy contains a cycle or a shared child.");
        const node = json.nodes?.[index];
        if (!node) throw new Error("The model hierarchy references a missing node.");
        visited.add(index);
        result.push({ index, path, depth, name: node.name || `Part ${index + 1}`, mesh: node.mesh });
        for (const child of node.children ?? []) {
            const name = json.nodes?.[child]?.name ?? "";
            visit(child, path ? `${path}/${name}` : name, depth + 1);
        }
    }
    visit(root, "", 0);
    return result;
}

function preparePlainHierarchy(json) {
    json.nodes = json.nodes ?? []; json.scenes = json.scenes ?? [{ nodes: [] }]; json.scene = json.scene ?? 0;
    const scene = json.scenes[json.scene];
    if (!scene?.nodes?.length) throw new Error("The model's selected scene is empty.");
    // A common root gives multi-root GLBs unambiguous asset-relative paths.
    const used = new Set(json.nodes.map(n => n.name));
    const index = json.nodes.length;
    json.nodes.push({ name: uniqueName("ModelVistaRoot", used), children: [...scene.nodes] });
    scene.nodes = [index];
    const visited = new Set();
    function rename(nodeIndex) {
        if (visited.has(nodeIndex)) throw new Error("The model hierarchy contains a cycle or a shared child.");
        visited.add(nodeIndex);
        const node = json.nodes[nodeIndex];
        if (!node) throw new Error("The model hierarchy references a missing node.");
        const siblingNames = new Set();
        for (const child of node.children ?? []) {
            const definition = json.nodes[child];
            if (!definition) throw new Error("The model hierarchy references a missing node.");
            const oldName = definition.name;
            definition.name = uniqueName(oldName || `Part ${child + 1}`, siblingNames);
            rename(child);
        }
    }
    rename(index);
    const materialNames = new Set();
    (json.materials ?? []).forEach((material, i) => { material.name = uniqueName(material.name || `Material ${i + 1}`, materialNames); });
    return json.nodes[index].name;
}

function addressFor(set) {
    return `${set.raw.Path ?? ""}|${String(set.raw.StateKind || set.raw.ContainerType || set.kind).toLowerCase()}`;
}

function targetSet(sets, target) {
    const type = String(target.StateKind || target.ContainerType || "").toLowerCase();
    const matches = sets.filter(set => (set.raw.Path ?? "") === (target.ContainerPath ?? "")
        && [set.kind.toLowerCase(), String(set.raw.StateKind || "").toLowerCase(), String(set.raw.ContainerType || "").toLowerCase()].includes(type));
    return matches[target.ContainerIndex ?? 0] ?? null;
}

export function importDocument(bytes, filename = "model.glb") {
    const source = readGlb(bytes);
    const json = copy(source.json);
    const extension = json.extensions?.[EXTENSION];
    if (extension && Number(extension.SchemaVersion ?? 1) > 2) {
        throw new Error("This GLB+ uses a newer State schema. Create can edit schema versions 1 and 2.");
    }
    if (!extension) {
        const rootName = preparePlainHierarchy(json);
        json.extensions = json.extensions ?? {};
        json.extensions[EXTENSION] = { SchemaVersion: 2, SourceRenderPipeline: "glTF", RootName: rootName, Containers: [] };
    }
    nodeTree(json);
    const sets = (json.extensions[EXTENSION].Containers ?? []).map(raw => {
        const kind = kindOf(raw), data = raw[kind];
        const options = (data?.Options ?? []).map(option => ({ id: id(), raw: copy(option) }));
        return { id: id(), kind, raw: copy(raw), options, defaultId: options[data?.CurrentOption ?? 0]?.id ?? null };
    });
    for (const set of sets.filter(s => s.kind === "Group")) {
        for (const option of set.options) {
            option.links = (option.raw.Targets ?? []).map(raw => {
                const target = targetSet(sets, raw);
                return { raw: copy(raw), setId: target?.id ?? null, optionId: target?.options[raw.SelectedOption]?.id ?? null };
            });
        }
    }
    return { version: 1, filename, json, sets };
}

export function serializeDocument(doc) {
    const json = copy(doc.json);
    const extension = json.extensions[EXTENSION];
    const counts = new Map(), occurrences = new Map();
    doc.sets.forEach(set => {
        const key = addressFor(set), index = counts.get(key) ?? 0;
        occurrences.set(set.id, index); counts.set(key, index + 1);
    });
    extension.Containers = doc.sets.map(set => {
        const raw = copy(set.raw);
        if (set.kind === "Unknown") return raw;
        const data = raw[set.kind];
        data.CurrentOption = set.options.findIndex(option => option.id === set.defaultId);
        data.Options = set.options.map(option => {
            const result = copy(option.raw);
            if (set.kind === "Group") {
                result.Targets = (option.links ?? []).map(link => {
                    const target = doc.sets.find(candidate => candidate.id === link.setId);
                    if (!target) return copy(link.raw);
                    return { ...copy(link.raw), ContainerPath: target.raw.Path ?? "", StateKind: target.raw.StateKind,
                        ContainerType: target.raw.ContainerType ?? target.raw.ModelVistaType, ContainerIndex: occurrences.get(target.id),
                        SelectedOption: target.options.findIndex(candidate => candidate.id === link.optionId) };
                });
            }
            return result;
        });
        return raw;
    });
    json.extensionsUsed = [...new Set([...(json.extensionsUsed ?? []), EXTENSION])];
    return json;
}

export function exportDocument(sourceBytes, doc, { thumbnails = true } = {}) {
    const issues = validateDocument(doc);
    if (issues.errors.length) throw new Error(issues.errors.join("\n"));
    const source = readGlb(sourceBytes), json = serializeDocument(doc);
    const binary = thumbnails ? embedThumbnails(source, json) : source.binaryChunk;
    return writeGlb(source, json, binary);
}

const vectorFinite = (value, fields) => value && fields.every(key => Number.isFinite(value[key]));

export function validateDocument(doc) {
    const errors = [], warnings = [];
    let tree;
    try { tree = nodeTree(doc.json); } catch (error) { return { errors: [error.message], warnings }; }
    const root = rootIndex(doc.json);
    const pathExists = path => typeof path === "string" && resolveModelVistaNodePath(doc.json, root, path) !== null;
    const seenIds = new Set();
    const ensureId = value => { if (!value || seenIds.has(value)) errors.push("The document contains duplicate or missing editing IDs."); seenIds.add(value); };
    for (const set of doc.sets) {
        ensureId(set.id);
        const label = nameOf(set);
        if (set.kind === "Unknown") { warnings.push("An unsupported State record will be preserved unchanged."); continue; }
        if (!EDITABLE_KINDS.includes(set.kind)) warnings.push(`${label}: ${set.kind} properties are preserved; use desktop Create for detailed editing.`);
        const data = dataOf(set);
        if (!set.options.length) errors.push(`${label}: add at least one option.`);
        if (!set.options.some(option => option.id === set.defaultId)) errors.push(`${label}: choose an initial option.`);
        if (!pathExists(set.raw.Path ?? "")) errors.push(`${label}: the State set's hierarchy path is missing.`);
        if (set.kind === "Transform" && !pathExists(data.TargetTransformPath)) errors.push(`${label}: choose a transform target.`);
        if (set.kind === "Camera" && !pathExists(data.ReferenceTransformPath ?? set.raw.Path ?? "")) errors.push(`${label}: the camera reference is missing.`);
        if (set.kind === "Material") {
            for (const target of data.TargetRenderers ?? [{ Path: set.raw.Path ?? "" }]) {
                if (!pathExists(target.Path)) errors.push(`${label}: a material target is missing.`);
                if (target.MaterialSlot != null) {
                    if (!Number.isInteger(target.MaterialSlot) || !materialSlots(doc, target.Path).some(slot => slot.index === target.MaterialSlot)) errors.push(`${label}: choose a valid material slot.`);
                    const warning = "Material-slot targets require the current web Viewer. Older Viewers and Unity Create may replace the whole renderer.";
                    if (!warnings.includes(warning)) warnings.push(warning);
                }
            }
        }
        const visiblePaths = new Set();
        for (const option of set.options) {
            ensureId(option.id);
            const value = option.raw, prefix = `${label} / ${optionName(option)}`;
            if (!String(value.DisplayName ?? "").trim()) errors.push(`${label}: every option needs a name.`);
            if (!Number.isFinite(value.TransitionDuration ?? 0) || (value.TransitionDuration ?? 0) < 0) errors.push(`${prefix}: transition duration must be a nonnegative number.`);
            if (["Transform", "Camera"].includes(set.kind)) {
                if (value.ApplyPosition && !vectorFinite(value.LocalPosition, ["X", "Y", "Z"])) errors.push(`${prefix}: position must contain three finite values.`);
                if (value.ApplyRotation && !vectorFinite(value.LocalRotation, ["X", "Y", "Z", "W"])) errors.push(`${prefix}: rotation is invalid.`);
                else if (value.ApplyRotation && Math.hypot(...Object.values(value.LocalRotation)) < 1e-8) warnings.push(`${prefix}: a legacy zero rotation is preserved and plays as an identity rotation.`);
                if (set.kind === "Camera" && value.ApplyFieldOfView && !(value.FieldOfView > 0 && value.FieldOfView < 180)) errors.push(`${prefix}: field of view must be between 0 and 180 degrees.`);
                if (set.kind === "Camera" && value.Orthographic && !(value.OrthographicSize > 0)) errors.push(`${prefix}: orthographic size must be positive.`);
            }
            if (set.kind === "Activation") {
                for (const path of value.TargetPaths ?? []) { visiblePaths.add(path); if (!pathExists(path)) errors.push(`${prefix}: visibility target '${path}' is missing.`); }
            }
            if (set.kind === "Material") {
                const records = doc.json.extensions[EXTENSION].Materials ?? [];
                const found = (Number.isInteger(value.MaterialId) && records.some(record => record.Id === value.MaterialId))
                    || doc.json.materials?.some(material => material.name === value.MaterialName);
                if (!found) errors.push(`${prefix}: choose a material.`);
            }
            if (set.kind === "Animation") {
                const index = value.AnimationIndex ?? -1;
                if (!Number.isInteger(index) || index < -1 || (index >= 0 && !animationClips(doc, set).some(clip => clip.index === index))) errors.push(`${prefix}: choose an animation clip available in this model, or Stop.`);
                if (!Number.isFinite(value.Speed ?? 1)) errors.push(`${prefix}: playback speed must be a finite number.`);
                if (!Number.isFinite(value.CrossFadeDuration ?? 0.25) || (value.CrossFadeDuration ?? 0.25) < 0) errors.push(`${prefix}: crossfade must be a nonnegative number.`);
            }
            if (set.kind === "Group") {
                for (const link of option.links ?? []) {
                    const target = doc.sets.find(candidate => candidate.id === link.setId);
                    if (!target?.options.some(candidate => candidate.id === link.optionId)) errors.push(`${prefix}: a linked State or option is missing.`);
                }
                const targets = (option.links ?? []).map(link => link.setId);
                if (new Set(targets).size !== targets.length) errors.push(`${prefix}: select only one option per linked State set.`);
            }
        }
        if (set.kind === "Activation" && !visiblePaths.size) errors.push(`${label}: at least one option must include a visible target.`);
    }
    const visited = new Set(), stack = new Set();
    function visit(set) {
        if (stack.has(set.id)) { errors.push(`${nameOf(set)}: Group States contain a circular link.`); return; }
        if (visited.has(set.id)) return;
        visited.add(set.id); stack.add(set.id);
        if (set.kind === "Group") for (const option of set.options) for (const link of option.links ?? []) {
            const target = doc.sets.find(candidate => candidate.id === link.setId);
            if (target) visit(target);
        }
        stack.delete(set.id);
    }
    doc.sets.forEach(visit);
    const duplicatePaths = tree.filter((node, i) => tree.findIndex(other => other.path === node.path) !== i);
    if (duplicatePaths.length) warnings.push("Some imported nodes share a hierarchy path. Choose unambiguous targets for new States.");
    return { errors: [...new Set(errors)], warnings: [...new Set(warnings)] };
}

export function createSet(doc, kind, { name, paths = [], transform, camera, material } = {}) {
    if (!EDITABLE_KINDS.includes(kind)) throw new Error("Choose a supported State type.");
    const tree = nodeTree(doc.json);
    if (paths.some(path => tree.filter(node => node.path === path).length !== 1)) throw new Error("Choose parts with unique hierarchy paths for this State.");
    const base = { DisplayName: "Option 1", TransitionDuration: kind === "Activation" ? 0 : 0.6, EaseMode: "EaseInOut" };
    const data = { ModelVistaName: name?.trim() || KIND_LABELS[kind], UiVisibility: "Visible", CurrentOption: 0 };
    const set = { id: id(), kind, raw: { Path: "", StateKind: kind.toLowerCase(), ContainerType: `InnoWorks.ModelVista.${kind}States, InnoWorks.ModelVista.Create`, ApplyOnAwake: true }, options: [], defaultId: null };
    set.raw[kind] = data;
    const option = raw => ({ id: id(), raw: { ...base, ...raw } });
    if (kind === "Activation") {
        if (!paths.length) throw new Error("Select the parts this visibility State controls.");
        set.options = [option({ DisplayName: "Visible", TargetPaths: [...paths] }), option({ DisplayName: "Hidden", TargetPaths: [] })];
    } else if (kind === "Transform") {
        if (paths.length !== 1) throw new Error("Select one part for this Transform State. Combine multiple transforms with a Group.");
        data.TargetTransformPath = paths[0];
        set.options = [option({ DisplayName: "Original", ApplyPosition: true, ApplyRotation: true, AnimationMode: "Time", ...transform })];
    } else if (kind === "Camera") {
        data.ReferenceTransformPath = ""; data.TargetCameraPath = "";
        set.options = [option({ DisplayName: "View 1", ApplyPosition: true, ApplyRotation: true, ApplyFieldOfView: true, ApplyProjection: true, ...camera })];
    } else if (kind === "Material") {
        if (!paths.length) throw new Error("Select the mesh parts this Material State controls.");
        data.TargetRenderers = paths.map(Path => ({ Path, ComponentType: "UnityEngine.Renderer, UnityEngine.CoreModule" }));
        set.options = [option({ DisplayName: "Finish 1", ...material })];
    } else if (kind === "Group") {
        data.TransitionTimingMode = "ParentGroupDuration";
        set.options = [{ ...option({ DisplayName: "Preset 1", Targets: [] }), links: [] }];
    } else if (kind === "Animation") {
        const clips = animationClips(doc);
        if (!clips.length) throw new Error("This model contains no embedded animation clips. Import an animated GLB or FBX, or use Transform States to author motion.");
        data.TargetAnimationPath = "";
        set.options = [option({ DisplayName: "Stop", AnimationIndex: -1, Speed: 1, Loop: false, CrossFadeDuration: 0, TransitionDuration: 0 }),
            ...clips.map(clip => option({ DisplayName: clip.name, AnimationIndex: clip.index, Speed: 1, Loop: true, CrossFadeDuration: 0.25, TransitionDuration: 0 }))];
    }
    set.defaultId = set.options[0].id;
    doc.sets.push(set);
    doc.deferredStateIds = [...(doc.deferredStateIds ?? []), set.id];
    return set;
}

export function duplicateOption(set, optionId) {
    const original = set.options.find(option => option.id === optionId);
    if (!original) throw new Error("Select an option first.");
    const option = { ...copy(original), id: id() };
    option.raw.DisplayName = `${optionName(original)} copy`;
    set.options.splice(set.options.indexOf(original) + 1, 0, option);
    return option;
}

export function duplicateSet(doc, setId) {
    const source = doc.sets.find(set => set.id === setId);
    const result = copy(source); result.id = id();
    const defaults = result.options.findIndex(option => option.id === result.defaultId);
    result.options.forEach(option => { option.id = id(); });
    result.defaultId = result.options[defaults]?.id;
    dataOf(result).ModelVistaName = nameOf(source) + " copy";
    doc.sets.splice(doc.sets.indexOf(source) + 1, 0, result);
    doc.deferredStateIds = [...(doc.deferredStateIds ?? []), result.id];
    return result;
}

export function removeOption(doc, set, optionId) {
    if (set.options.length <= 1) throw new Error("Keep at least one option, or delete the State set.");
    assertNotReferenced(doc, set.id, optionId);
    set.options = set.options.filter(option => option.id !== optionId);
    if (set.defaultId === optionId) set.defaultId = set.options[0].id;
}

export function removeSet(doc, setId) {
    assertNotReferenced(doc, setId);
    doc.sets = doc.sets.filter(set => set.id !== setId);
}

function assertNotReferenced(doc, setId, optionId) {
    for (const set of doc.sets.filter(set => set.kind === "Group")) for (const option of set.options) {
        if ((option.links ?? []).some(link => link.setId === setId && (!optionId || link.optionId === optionId))) {
            throw new Error(`Remove the link in ${nameOf(set)} / ${optionName(option)} before deleting this item.`);
        }
    }
}

export function moveItem(items, itemId, direction) {
    const from = items.findIndex(item => item.id === itemId), to = from + direction;
    if (from < 0 || to < 0 || to >= items.length) return;
    [items[from], items[to]] = [items[to], items[from]];
}

export function addSolidMaterial(doc, name, color, metallic, roughness) {
    if (![...color, metallic, roughness].every(value => Number.isFinite(value) && value >= 0 && value <= 1)) throw new Error("Material values must be between 0 and 1.");
    const extension = doc.json.extensions[EXTENSION]; extension.Materials = extension.Materials ?? [];
    const Id = Math.max(-1, ...extension.Materials.map(record => Number(record.Id) || 0)) + 1;
    const names = new Set([...(doc.json.materials ?? []).map(material => material.name), ...extension.Materials.map(material => material.Name)]);
    const Name = uniqueName(name?.trim() || `Finish ${Id + 1}`, names);
    extension.Materials.push({ Id, Name, ShaderName: "glTF-pbrMetallicRoughness", SourceRenderPipeline: "glTF",
        Properties: [
            { Name: "baseColorFactor", Type: "Color", VectorValue: { X: color[0], Y: color[1], Z: color[2], W: 1 } },
            { Name: "metallicFactor", Type: "Range", FloatValue: metallic },
            { Name: "roughnessFactor", Type: "Range", FloatValue: roughness }
        ] });
    return { MaterialId: Id, MaterialName: Name, ShaderName: "glTF-pbrMetallicRoughness" };
}
